"use strict";
/**
 * Parallel Markets Modal Handler
 *
 * Lets the user attach buy-side parallel markets to the current primary market.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.openParallelMarketsModal = openParallelMarketsModal;
exports.setupParallelMarketsModal = setupParallelMarketsModal;
const state_1 = require("../../state");
const ui_1 = require("../../ui");
const kalshi_1 = require("../../api/kalshi");
const parallel_markets_service_1 = require("../../services/parallel-markets-service");
const polymarket_us_1 = require("../../api/polymarket-us");
const data_source_service_1 = require("../../services/data-source-service");
const novig_1 = require("../../api/novig");
const cache_1 = require("../../markets/cache");
const constants_1 = require("../../markets/constants");
const market_sides_1 = require("./shared/market-sides");
const parallel_quick_add_1 = require("./parallel-quick-add");
const novig_adapters_1 = require("./shared/novig-adapters");
const pm_us_adapters_1 = require("./shared/pm-us-adapters");
let modalState = {
    venue: "kalshi",
    events: [],
    expandedEventTicker: null,
    expandedMarkets: [],
    selectedMarkets: [],
    selectedEventTickers: new Map(),
    marketFilterTerm: "",
    marketFilterSelectionStart: null,
    marketFilterSelectionEnd: null,
    eventFilterTerm: "",
};
function resetModalState() {
    modalState = {
        venue: "kalshi",
        events: [],
        expandedEventTicker: null,
        expandedMarkets: [],
        selectedMarkets: [],
        selectedEventTickers: new Map(),
        marketFilterTerm: "",
        marketFilterSelectionStart: null,
        marketFilterSelectionEnd: null,
        eventFilterTerm: "",
    };
}
function escapeHtml(value) {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
function normalizeSeriesTicker(value) {
    return value?.trim().toUpperCase() ?? "";
}
function formatSeriesTickerInput(value) {
    return value.toUpperCase();
}
function getPrimaryMarketId() {
    return (0, state_1.getState)().selectedMarket?.id ?? null;
}
function getPrimaryMarketVenue() {
    const dataSource = (0, state_1.getState)().dataSource;
    if (dataSource === "kalshi" || dataSource === "pm-us" || dataSource === "novig") {
        return dataSource;
    }
    return null;
}
function getCurrentSelections() {
    return (0, parallel_markets_service_1.getParallelMarkets)(getPrimaryMarketId());
}
/**
 * "Steelers = [Browns | Steelers]" — which of the leg's sides is the same
 * outcome as the click's YES (phase 9.2). Venues order sides differently, so
 * this is the operator's call, per leg.
 */
function legSideToggle(selection) {
    const primaryYes = (0, state_1.getState)().selectedMarket?.yesSubtitle?.trim() || "YES";
    const row = document.createElement("div");
    row.className = "parallel-leg-side";
    const prompt = document.createElement("span");
    prompt.className = "parallel-markets-selected-meta parallel-leg-side-prompt";
    prompt.textContent = `${primaryYes} =`;
    row.appendChild(prompt);
    const control = document.createElement("div");
    control.className = "parallel-leg-switch";
    row.appendChild(control);
    (0, parallel_markets_service_1.legSideLabels)(selection.sideNames).forEach((label, i) => {
        const invert = i === 1;
        const button = document.createElement("button");
        button.type = "button";
        button.className = "parallel-leg-switch-option";
        const active = (selection.invert ?? false) === invert;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
        button.textContent = label;
        button.addEventListener("click", () => {
            const primaryMarketId = getPrimaryMarketId();
            if (!primaryMarketId)
                return;
            const next = (0, parallel_markets_service_1.setParallelMarketInvert)(primaryMarketId, selection.marketId, selection.venue, invert);
            (0, state_1.updateState)({ parallelMarkets: next });
            renderSelectionList();
        });
        control.appendChild(button);
    });
    return row;
}
function renderSelectionList() {
    const list = document.getElementById("parallel-markets-selected-list");
    const clearBtn = document.getElementById("parallel-markets-clear-btn");
    if (!list)
        return;
    const primaryMarketId = getPrimaryMarketId();
    const selections = getCurrentSelections();
    list.innerHTML = "";
    if (clearBtn) {
        clearBtn.disabled = !primaryMarketId || selections.length === 0;
    }
    if (selections.length === 0) {
        list.innerHTML =
            '<p class="parallel-markets-empty">No parallel markets selected.</p>';
        return;
    }
    for (const selection of selections) {
        const item = document.createElement("div");
        item.className = "parallel-markets-selected-item";
        const title = (selection.marketTitle || selection.marketId).slice(0, 50);
        const head = document.createElement("div");
        head.className = "parallel-leg-head";
        head.innerHTML = `
			<span class="parallel-markets-selected-title">${escapeHtml(title)}</span>
			<span class="parallel-markets-selected-meta parallel-leg-venue">${parallel_markets_service_1.PARALLEL_VENUE_TAGS[selection.venue]}</span>
		`;
        const removeBtn = document.createElement("button");
        removeBtn.type = "button";
        removeBtn.className = "parallel-markets-remove-btn parallel-leg-remove";
        removeBtn.textContent = "✕";
        removeBtn.setAttribute("aria-label", `Remove ${title}`);
        removeBtn.addEventListener("click", () => {
            if (!primaryMarketId)
                return;
            const nextSelections = (0, parallel_markets_service_1.removeParallelMarket)(primaryMarketId, selection.marketId, selection.venue);
            (0, state_1.updateState)({ parallelMarkets: nextSelections });
            renderSelectionList();
            renderEventResults();
        });
        head.appendChild(removeBtn);
        item.appendChild(head);
        item.appendChild(legSideToggle(selection));
        list.appendChild(item);
    }
}
function applyVenueUI() {
    const kalshiBtn = document.getElementById("parallel-market-venue-kalshi");
    const pmUsBtn = document.getElementById("parallel-market-venue-pm-us");
    const kalshiControls = document.getElementById("parallel-market-kalshi-controls");
    const pmUsControls = document.getElementById("parallel-market-pm-us-controls");
    kalshiBtn?.classList.toggle("active", modalState.venue === "kalshi");
    pmUsBtn?.classList.toggle("active", modalState.venue === "pm-us");
    kalshiControls?.classList.toggle("hidden", modalState.venue !== "kalshi");
    pmUsControls?.classList.toggle("hidden", modalState.venue !== "pm-us");
    document.getElementById("parallel-market-venue-novig")?.classList.toggle("active", modalState.venue === "novig");
    document
        .getElementById("parallel-market-novig-controls")
        ?.classList.toggle("hidden", modalState.venue !== "novig");
}
function getNovigLeagueSelect() {
    return document.getElementById("parallel-market-novig-league-select");
}
/** Novig's leagues, preselecting `selected` when it is one of them. */
async function loadNovigLeagues(selected = "") {
    const select = getNovigLeagueSelect();
    if (!select)
        return;
    select.innerHTML = '<option value="">Loading leagues...</option>';
    select.disabled = true;
    try {
        const leagues = await (0, novig_1.listNovigLeagues)();
        select.innerHTML = '<option value="">Select a league</option>';
        for (const league of leagues) {
            const option = document.createElement("option");
            option.value = league;
            option.textContent = (0, constants_1.novigLeagueLabel)(league);
            select.appendChild(option);
        }
        select.value = leagues.includes(selected) ? selected : "";
        select.disabled = false;
    }
    catch {
        select.innerHTML = '<option value="">Failed to load leagues</option>';
    }
}
async function searchNovigEvents() {
    const league = getNovigLeagueSelect()?.value ?? "";
    if (!league) {
        setResultsMessage("Select a Novig league to load events.");
        return;
    }
    try {
        modalState.events = (await (0, novig_1.listNovigEvents)(league)).map(novig_adapters_1.novigEventToEvent);
        modalState.expandedEventTicker = null;
        modalState.expandedMarkets = [];
        clearEventSearch();
        renderEventResults();
    }
    catch {
        setResultsMessage("Failed to load Novig events.");
    }
}
function getKalshiSeriesSelect() {
    return document.getElementById("parallel-market-kalshi-series-select");
}
function getKalshiSeriesInput() {
    return document.getElementById("parallel-market-series-input");
}
function getEventSearchInputs() {
    return [
        document.getElementById("parallel-market-kalshi-event-search"),
        document.getElementById("parallel-market-pm-us-event-search"),
        document.getElementById("parallel-market-novig-event-search"),
    ].filter((input) => Boolean(input));
}
function syncEventSearchInputs() {
    for (const input of getEventSearchInputs()) {
        if (input.value !== modalState.eventFilterTerm) {
            input.value = modalState.eventFilterTerm;
        }
    }
}
function clearEventSearch() {
    modalState.eventFilterTerm = "";
    syncEventSearchInputs();
}
function toggleKalshiCustomInput() {
    const select = getKalshiSeriesSelect();
    const input = getKalshiSeriesInput();
    if (!select || !input)
        return;
    const showCustomInput = select.value === data_source_service_1.CUSTOM_KALSHI_LEAGUE_VALUE;
    input.classList.toggle("hidden", !showCustomInput);
    input.disabled = !showCustomInput;
    if (!showCustomInput) {
        input.value = "";
    }
}
function populateKalshiSeriesOptions(selectedSeries = "") {
    const select = getKalshiSeriesSelect();
    const input = getKalshiSeriesInput();
    if (!select)
        return;
    (0, data_source_service_1.populateKalshiLeagues)(select);
    const normalizedSeries = normalizeSeriesTicker(selectedSeries);
    if (input) {
        input.value = "";
    }
    if (normalizedSeries &&
        select.querySelector(`option[value="${CSS.escape(normalizedSeries)}"]`)) {
        select.value = normalizedSeries;
    }
    else if (normalizedSeries) {
        select.value = data_source_service_1.CUSTOM_KALSHI_LEAGUE_VALUE;
        if (input) {
            input.value = normalizedSeries;
        }
    }
    toggleKalshiCustomInput();
}
function getKalshiSeriesValue() {
    const select = getKalshiSeriesSelect();
    const input = getKalshiSeriesInput();
    if (select?.value === data_source_service_1.CUSTOM_KALSHI_LEAGUE_VALUE) {
        return normalizeSeriesTicker(input?.value);
    }
    return select?.value.trim() ?? normalizeSeriesTicker(input?.value);
}
async function loadPmUsSports() {
    const select = document.getElementById("parallel-market-sport-select");
    if (!select)
        return;
    select.innerHTML = '<option value="">Loading sports...</option>';
    select.disabled = true;
    try {
        const response = await (0, polymarket_us_1.listPolymarketUsSports)();
        const sports = (0, pm_us_adapters_1.normalizeSportsResponse)(response.data);
        select.innerHTML = '<option value="">Select a sport</option>';
        for (const sport of sports) {
            const option = document.createElement("option");
            option.value = String(sport.slug ?? sport.sport ?? sport.name ?? "");
            option.textContent = String(sport.sport ?? sport.name ?? sport.slug ?? "Unknown");
            select.appendChild(option);
        }
        select.disabled = false;
    }
    catch {
        select.innerHTML = '<option value="">Failed to load sports</option>';
        select.disabled = true;
    }
}
function setResultsMessage(message) {
    const results = document.getElementById("parallel-market-results");
    if (!results)
        return;
    results.innerHTML = `<p class="parallel-markets-empty">${escapeHtml(message)}</p>`;
}
/** A row's subtext: the market type when the venue names one (Novig), so it can be searched. */
function getMarketMeta(market) {
    if (market.marketCategoryLabel)
        return market.marketCategoryLabel;
    return market.ticker === getDisplayTitle(market) ? market.subtitle : market.ticker;
}
function getDisplayTitle(market) {
    if (market.title && market.title.length < 60)
        return market.title;
    // A Novig ticker is a UUID: a cut title reads better than it.
    if (modalState.venue === "novig")
        return `${market.title.slice(0, 57)}...`;
    return market.ticker || `${market.title.slice(0, 57)}...`;
}
function isUnavailableSelection(market) {
    const primaryMarketId = getPrimaryMarketId();
    if (market.ticker === primaryMarketId) {
        return true;
    }
    return getCurrentSelections().some((selection) => selection.marketId === market.ticker &&
        selection.venue === modalState.venue);
}
function updateAddButton() {
    const addBtn = document.getElementById("parallel-markets-add-btn");
    if (!addBtn)
        return;
    addBtn.disabled = modalState.selectedMarkets.length === 0;
    addBtn.textContent =
        modalState.selectedMarkets.length === 0
            ? "Add Selected"
            : `Add Selected (${modalState.selectedMarkets.length})`;
}
function renderMarketsForEvent(container, eventTicker) {
    if (modalState.expandedMarkets.length === 0) {
        container.innerHTML =
            '<p class="parallel-markets-empty">Loading markets...</p>';
        return;
    }
    const filterRow = document.createElement("div");
    filterRow.className = "parallel-markets-market-filter";
    const filterInput = document.createElement("input");
    filterInput.type = "text";
    filterInput.className =
        "parallel-markets-input parallel-markets-event-search-input";
    filterInput.placeholder = "Filter markets...";
    filterInput.value = modalState.marketFilterTerm;
    filterInput.addEventListener("input", () => {
        modalState.marketFilterTerm = filterInput.value;
        modalState.marketFilterSelectionStart = filterInput.selectionStart;
        modalState.marketFilterSelectionEnd = filterInput.selectionEnd;
        renderEventResults();
        const results = document.getElementById("parallel-market-results");
        const replacementInput = results?.querySelector(".parallel-markets-market-filter input");
        if (replacementInput) {
            const start = modalState.marketFilterSelectionStart ??
                modalState.marketFilterTerm.length;
            const end = modalState.marketFilterSelectionEnd ??
                modalState.marketFilterTerm.length;
            replacementInput.focus();
            replacementInput.setSelectionRange(Math.min(start, replacementInput.value.length), Math.min(end, replacementInput.value.length));
        }
    });
    filterRow.appendChild(filterInput);
    container.appendChild(filterRow);
    const subtitlePriority = (market) => {
        const subtitle = (market.yes_sub_title || market.subtitle || "")
            .trim()
            .toLowerCase();
        if (subtitle === "moneyline")
            return 0;
        if (subtitle === "totals")
            return 1;
        if (subtitle === "spreads")
            return 2;
        return 3;
    };
    const filteredMarkets = modalState.expandedMarkets.filter((market) => {
        if (market.status === "finalized")
            return false;
        const term = modalState.marketFilterTerm.trim().toLowerCase();
        if (!term)
            return true;
        return (getDisplayTitle(market).toLowerCase().includes(term) ||
            (market.marketCategoryLabel ?? "").toLowerCase().includes(term) ||
            (market.yes_sub_title || market.subtitle || "")
                .toLowerCase()
                .includes(term));
    });
    filteredMarkets.sort((a, b) => {
        const priorityDiff = subtitlePriority(a) - subtitlePriority(b);
        if (priorityDiff !== 0)
            return priorityDiff;
        return getDisplayTitle(a).localeCompare(getDisplayTitle(b));
    });
    if (filteredMarkets.length === 0) {
        const empty = document.createElement("p");
        empty.className = "parallel-markets-empty";
        empty.textContent = "No markets match the current filter.";
        container.appendChild(empty);
        return;
    }
    for (const market of filteredMarkets) {
        const row = document.createElement("label");
        row.className = "parallel-markets-market-item";
        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.checked = modalState.selectedMarkets.some((selected) => selected.ticker === market.ticker);
        const unavailable = isUnavailableSelection(market);
        checkbox.disabled = unavailable;
        checkbox.addEventListener("change", () => {
            if (checkbox.checked) {
                modalState.selectedMarkets.push(market);
                modalState.selectedEventTickers.set(market.ticker, eventTicker);
            }
            else {
                modalState.selectedMarkets = modalState.selectedMarkets.filter((selected) => selected.ticker !== market.ticker);
                modalState.selectedEventTickers.delete(market.ticker);
            }
            updateAddButton();
        });
        const text = document.createElement("span");
        text.innerHTML = `
			<span class="parallel-markets-market-title">${escapeHtml(getDisplayTitle(market))}</span>
			<span class="parallel-markets-market-meta">${escapeHtml(getMarketMeta(market))}${unavailable ? " · already selected" : ""}</span>
		`;
        row.appendChild(checkbox);
        row.appendChild(text);
        container.appendChild(row);
    }
}
async function toggleEventExpand(eventTicker) {
    if (modalState.expandedEventTicker === eventTicker) {
        modalState.expandedEventTicker = null;
        modalState.expandedMarkets = [];
        modalState.marketFilterTerm = "";
        renderEventResults();
        return;
    }
    modalState.expandedEventTicker = eventTicker;
    modalState.expandedMarkets = [];
    modalState.marketFilterTerm = "";
    renderEventResults();
    try {
        let markets;
        if (modalState.venue === "pm-us") {
            // PM-US markets are loaded per event, same as Kalshi: the event
            // list deliberately arrives without them.
            const response = await (0, polymarket_us_1.getPolymarketUsEventMarkets)(eventTicker);
            markets = (response.data?.markets ?? []).map((market) => (0, pm_us_adapters_1.pmUsMarketToMarketFromEvent)(market, eventTicker));
        }
        else if (modalState.venue === "novig") {
            markets = (await (0, novig_1.getNovigEventMarkets)(eventTicker)).map((market) => (0, novig_adapters_1.novigMarketToMarketFromEvent)(market, eventTicker));
        }
        else {
            const response = await (0, kalshi_1.getMarketsByEvent)(eventTicker);
            markets = response.markets ?? [];
        }
        // Discard a late response for an event the operator already collapsed
        // or navigated away from.
        if (modalState.expandedEventTicker !== eventTicker)
            return;
        modalState.expandedMarkets = markets;
    }
    catch {
        if (modalState.expandedEventTicker !== eventTicker)
            return;
        modalState.expandedMarkets = [];
    }
    renderEventResults();
}
function renderEventResults() {
    const results = document.getElementById("parallel-market-results");
    if (!results)
        return;
    results.innerHTML = "";
    if (modalState.events.length === 0) {
        results.innerHTML =
            '<p class="parallel-markets-empty">No events loaded yet.</p>';
        return;
    }
    const eventFilterTerm = modalState.eventFilterTerm.trim().toLowerCase();
    const filteredEvents = modalState.events.filter((event) => {
        if (!eventFilterTerm)
            return true;
        return (event.title.toLowerCase().includes(eventFilterTerm) ||
            event.event_ticker.toLowerCase().includes(eventFilterTerm));
    });
    if (filteredEvents.length === 0) {
        results.innerHTML =
            '<p class="parallel-markets-empty">No events match the current search.</p>';
        return;
    }
    for (const event of filteredEvents) {
        const item = document.createElement("div");
        item.className = "parallel-markets-event-item";
        const header = document.createElement("button");
        header.type = "button";
        header.className = "parallel-markets-event-header";
        header.innerHTML = `
			<span>
				<span class="parallel-markets-event-title">${escapeHtml(event.title)}</span>
				<span class="parallel-markets-event-meta">${escapeHtml(modalState.venue === "novig" ? (event.sub_title ?? "") : event.event_ticker)}</span>
			</span>
			<span>${modalState.expandedEventTicker === event.event_ticker ? "▼" : "▶"}</span>
		`;
        header.addEventListener("click", () => {
            void toggleEventExpand(event.event_ticker);
        });
        item.appendChild(header);
        if (modalState.expandedEventTicker === event.event_ticker) {
            const markets = document.createElement("div");
            markets.className = "parallel-markets-market-list";
            renderMarketsForEvent(markets, event.event_ticker);
            item.appendChild(markets);
        }
        results.appendChild(item);
    }
}
async function searchKalshiEvents() {
    const seriesTicker = getKalshiSeriesValue();
    if (!seriesTicker) {
        setResultsMessage("Select or enter a Kalshi series ticker to load events.");
        return;
    }
    try {
        if (getKalshiSeriesSelect()?.value === data_source_service_1.CUSTOM_KALSHI_LEAGUE_VALUE) {
            (0, state_1.saveCustomSeries)(seriesTicker);
            populateKalshiSeriesOptions(seriesTicker);
        }
        const response = await (0, kalshi_1.getEvents)(50, seriesTicker, undefined, false, "open");
        modalState.events = response.events ?? [];
        modalState.expandedEventTicker = null;
        modalState.expandedMarkets = [];
        clearEventSearch();
        renderEventResults();
    }
    catch {
        setResultsMessage("Failed to load Kalshi events.");
    }
}
async function searchPmUsEvents() {
    const select = document.getElementById("parallel-market-sport-select");
    const tagSlug = select?.value ?? "";
    if (!tagSlug) {
        setResultsMessage("Select a PM-US sport to load events.");
        return;
    }
    try {
        const response = await (0, polymarket_us_1.listPolymarketUsEvents)({
            limit: pm_us_adapters_1.PM_US_EVENTS_LIMIT,
            active: true,
            closed: false,
            tagSlug,
            gamesOnly: true,
        });
        const events = (0, pm_us_adapters_1.normalizePmUsEventsResponse)(response.data);
        modalState.events = events.map(pm_us_adapters_1.pmUsEventToEvent);
        modalState.expandedEventTicker = null;
        modalState.expandedMarkets = [];
        clearEventSearch();
        renderEventResults();
    }
    catch {
        setResultsMessage("Failed to load PM-US events.");
    }
}
function syncStateFromStorage() {
    (0, state_1.updateState)({
        parallelMarkets: getCurrentSelections(),
    });
}
function addSelectedMarkets() {
    const primaryMarketId = getPrimaryMarketId();
    if (!primaryMarketId || modalState.selectedMarkets.length === 0) {
        return;
    }
    const nextSelections = (0, parallel_markets_service_1.addParallelMarkets)(primaryMarketId, modalState.selectedMarkets.map((market) => ({
        marketId: market.ticker,
        marketTitle: market.title,
        eventTicker: modalState.selectedEventTickers.get(market.ticker) ??
            market.event_ticker,
        venue: modalState.venue,
        sideNames: (0, market_sides_1.marketSideNames)(market),
        ...(market.novigOutcomeIds ? { novigOutcomeIds: market.novigOutcomeIds } : {}),
    })));
    (0, state_1.updateState)({ parallelMarkets: nextSelections });
    modalState.selectedMarkets = [];
    modalState.selectedEventTickers = new Map();
    updateAddButton();
    renderSelectionList();
    renderEventResults();
}
function clearSelectionsForPrimary() {
    const primaryMarketId = getPrimaryMarketId();
    if (!primaryMarketId) {
        return;
    }
    (0, parallel_markets_service_1.clearParallelMarkets)(primaryMarketId);
    syncStateFromStorage();
    renderSelectionList();
    renderEventResults();
}
async function openParallelMarketsModal() {
    resetModalState();
    const selectedMarket = (0, state_1.getState)().selectedMarket;
    const primaryVenue = getPrimaryMarketVenue();
    if (!selectedMarket || !primaryVenue) {
        return;
    }
    modalState.venue = primaryVenue;
    applyVenueUI();
    renderSelectionList();
    updateAddButton();
    const seriesInput = document.getElementById("parallel-market-series-input");
    if (seriesInput) {
        seriesInput.value = "";
    }
    populateKalshiSeriesOptions(selectedMarket.category ?? "");
    clearEventSearch();
    await loadPmUsSports();
    (0, ui_1.showParallelMarketsModal)();
    // Auto-load events based on the current venue and default selection
    if (modalState.venue === "kalshi") {
        const seriesTicker = getKalshiSeriesValue();
        if (seriesTicker && seriesTicker !== data_source_service_1.CUSTOM_KALSHI_LEAGUE_VALUE) {
            void searchKalshiEvents();
            return;
        }
    }
    else if (modalState.venue === "pm-us") {
        void searchPmUsEvents();
        return;
    }
    else if (modalState.venue === "novig") {
        // The league being browsed — a leg is usually in the same game.
        await loadNovigLeagues((0, cache_1.getCurrentCategory)());
        void searchNovigEvents();
        return;
    }
    setResultsMessage("Select a series to load events.");
}
function setupParallelMarketsModal() {
    (0, parallel_quick_add_1.setupParallelQuickAdd)();
    const openBtn = document.getElementById("open-parallel-markets-btn");
    openBtn?.addEventListener("click", () => {
        (0, ui_1.hideMarketSelectionModal)();
        void openParallelMarketsModal();
    });
    const closeBtn = document.getElementById("close-parallel-markets-modal");
    closeBtn?.addEventListener("click", () => (0, ui_1.hideParallelMarketsModal)());
    const doneBtn = document.getElementById("parallel-markets-done-btn");
    doneBtn?.addEventListener("click", () => (0, ui_1.hideParallelMarketsModal)());
    const overlay = document.querySelector(".parallel-markets-modal-overlay");
    overlay?.addEventListener("click", (event) => {
        if (event.target === overlay) {
            (0, ui_1.hideParallelMarketsModal)();
        }
    });
    const kalshiBtn = document.getElementById("parallel-market-venue-kalshi");
    kalshiBtn?.addEventListener("click", () => {
        modalState.venue = "kalshi";
        modalState.events = [];
        modalState.expandedEventTicker = null;
        modalState.expandedMarkets = [];
        modalState.selectedMarkets = [];
        modalState.selectedEventTickers = new Map();
        clearEventSearch();
        populateKalshiSeriesOptions((0, state_1.getState)().selectedMarket?.category ?? "");
        applyVenueUI();
        updateAddButton();
        const seriesTicker = getKalshiSeriesValue();
        if (seriesTicker && seriesTicker !== data_source_service_1.CUSTOM_KALSHI_LEAGUE_VALUE) {
            void searchKalshiEvents();
        }
        else {
            setResultsMessage("Select a series to load events.");
        }
    });
    const pmUsBtn = document.getElementById("parallel-market-venue-pm-us");
    pmUsBtn?.addEventListener("click", () => {
        modalState.venue = "pm-us";
        modalState.events = [];
        modalState.expandedEventTicker = null;
        modalState.expandedMarkets = [];
        modalState.selectedMarkets = [];
        modalState.selectedEventTickers = new Map();
        clearEventSearch();
        applyVenueUI();
        updateAddButton();
        void searchPmUsEvents();
    });
    document.getElementById("parallel-market-venue-novig")?.addEventListener("click", () => {
        modalState.venue = "novig";
        modalState.events = [];
        modalState.expandedEventTicker = null;
        modalState.expandedMarkets = [];
        modalState.selectedMarkets = [];
        modalState.selectedEventTickers = new Map();
        clearEventSearch();
        applyVenueUI();
        updateAddButton();
        void loadNovigLeagues(getNovigLeagueSelect()?.value ?? "").then(() => searchNovigEvents());
    });
    getNovigLeagueSelect()?.addEventListener("change", () => {
        void searchNovigEvents();
    });
    const kalshiSeriesSelect = getKalshiSeriesSelect();
    kalshiSeriesSelect?.addEventListener("change", () => {
        toggleKalshiCustomInput();
        if (kalshiSeriesSelect.value !== data_source_service_1.CUSTOM_KALSHI_LEAGUE_VALUE) {
            void searchKalshiEvents();
        }
    });
    const kalshiSeriesInput = getKalshiSeriesInput();
    kalshiSeriesInput?.addEventListener("input", () => {
        const nextValue = formatSeriesTickerInput(kalshiSeriesInput.value);
        if (nextValue === kalshiSeriesInput.value) {
            return;
        }
        const { selectionStart, selectionEnd } = kalshiSeriesInput;
        kalshiSeriesInput.value = nextValue;
        if (selectionStart !== null && selectionEnd !== null) {
            kalshiSeriesInput.setSelectionRange(selectionStart, selectionEnd);
        }
    });
    kalshiSeriesInput?.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
            void searchKalshiEvents();
        }
    });
    const pmUsSearchSelect = document.getElementById("parallel-market-sport-select");
    pmUsSearchSelect?.addEventListener("change", () => {
        void searchPmUsEvents();
    });
    const kalshiSearchBtn = document.getElementById("parallel-market-kalshi-search-btn");
    kalshiSearchBtn?.addEventListener("click", () => {
        void searchKalshiEvents();
    });
    const pmUsSearchBtn = document.getElementById("parallel-market-pm-us-search-btn");
    pmUsSearchBtn?.addEventListener("click", () => {
        void searchPmUsEvents();
    });
    for (const input of getEventSearchInputs()) {
        input.addEventListener("input", () => {
            modalState.eventFilterTerm = input.value;
            syncEventSearchInputs();
            renderEventResults();
        });
    }
    const addBtn = document.getElementById("parallel-markets-add-btn");
    addBtn?.addEventListener("click", () => addSelectedMarkets());
    const clearBtn = document.getElementById("parallel-markets-clear-btn");
    clearBtn?.addEventListener("click", () => clearSelectionsForPrimary());
}
